import { computed, onActivated, onBeforeMount, onBeforeUnmount, onDeactivated, unref, watch } from 'vue'

let spotTickerConsumerCount = 0 // 统计当前前端有多少个组件/hook 正在使用现货行情。

/**
 * 解析普通值、ref/computed 或 getter 函数的当前值。
 * @param {*} source 待解析的数据源。
 * @returns {*} 返回数据源当前值。
 */
const resolveSourceValue = source => typeof source === 'function' ? source() : unref(source)

/**
 * 确保现货 ticker 快照已初始化。
 * 推送只负责增量更新，冷启动时仍需要接口快照作为基础列表。
 * @param {ReturnType<typeof useQuotesStore>} quotesStore 行情 store 实例。
 * @returns {void}
 */
const ensureSpotTickerSnapshot = (quotesStore) => {
  if (quotesStore.tickers?.length) {
    return
  }

  quotesStore.getTickers()
    .catch(error => {
      console.error('get spot tickers error', error)
    })
}

/**
 * 增加一个现货 ticker 使用者。
 * 第一个使用者进入时订阅后端 `alpha-market-ticker`，后续使用者只增加计数。
 * @returns {void}
 */
const startSpotTicker = () => {
  const quotesStore = useQuotesStore()

  ensureSpotTickerSnapshot(quotesStore)

  spotTickerConsumerCount++
  if (spotTickerConsumerCount > 1) {
    return
  }

  quotesStore.subPrice()
}

/**
 * 减少一个现货 ticker 使用者。
 * 最后一个使用者离开时取消后端 `alpha-market-ticker` 订阅。
 * @returns {void}
 */
const stopSpotTicker = () => {
  spotTickerConsumerCount = Math.max(spotTickerConsumerCount - 1, 0)
  if (spotTickerConsumerCount !== 0) {
    return
  }

  const quotesStore = useQuotesStore()
  quotesStore.unsubPrice()
}

/**
 * 绑定组件生命周期与现货 ticker 使用状态。
 * 支持 keep-alive：组件停用时释放，重新激活时按 enabled 状态恢复。
 * @param {Object} [options] 配置项。
 * @param {boolean|import('vue').Ref<boolean>|Function} [options.enabled=true] 是否启用订阅，可传布尔值、ref/computed 或 getter。
 * @returns {void}
 */
const useSpotTickerUsage = ({ enabled = true } = {}) => {
  let isUsing = false // 表示这个组件当前有没有“已经占用一个行情订阅名额”。
  let isActive = false // 表示组件生命周期状态：这个组件当前是不是“活着/激活中”。
  const isEnabled = () => Boolean(resolveSourceValue(enabled))

  const start = () => {
    if (!isActive || isUsing || !isEnabled()) {
      return
    }

    startSpotTicker()
    isUsing = true
  }

  const stop = () => {
    if (!isUsing) {
      return
    }

    stopSpotTicker()
    isUsing = false
  }

  const activate = () => {
    isActive = true
    sync()
  }

  const deactivate = () => {
    isActive = false
    stop()
  }

  const sync = () => {
    if (isEnabled()) {
      start()
    } else {
      stop()
    }
  }

  onBeforeMount(activate)
  onActivated(activate)
  onDeactivated(deactivate)
  onBeforeUnmount(deactivate)
  watch(() => resolveSourceValue(enabled), sync)
}

/**
 * 使用全量现货 ticker 列表，并自动持有现货 ticker 订阅。
 * @param {Object} [options] 配置项。
 * @param {boolean|import('vue').Ref<boolean>|Function} [options.enabled=true] 是否启用订阅。
 * @returns {import('vue').ComputedRef<Array>} 现货 ticker 列表。
 */
export const useSpotTickers = (options = {}) => {
  const quotesStore = useQuotesStore()

  useSpotTickerUsage(options)

  return computed(() => quotesStore.tickers || [])
}

/**
 * 使用单个现货币对 ticker，并自动持有现货 ticker 订阅。
 * 注意：后端仍是全量 `alpha-market-ticker`，这里仅在前端按 symbol 取单个币对。
 * @param {string|import('vue').Ref<string>|Function} symbol 币对标识，可传字符串、ref/computed 或 getter。
 * @param {Object} [options] 配置项。
 * @param {boolean|import('vue').Ref<boolean>|Function} [options.enabled=true] 是否启用订阅。
 * @returns {import('vue').ComputedRef<Object|null>} 单个现货 ticker，未命中时返回 null。
 */
export const useSpotTicker = (symbol, options = {}) => {
  const quotesStore = useQuotesStore()

  useSpotTickerUsage(options)

  return computed(() => {
    const coinMarket = resolveSourceValue(symbol)

    return coinMarket ? quotesStore.getSpotTicker(coinMarket) : null
  })
}
